import { useState } from "react";
import { api, formatError } from "@/lib/api";
import { limpiarPersona, obligatorios, personaVacia } from "@/lib/registro";
import PersonFields from "@/components/registry/PersonFields";
import { AvisoTablas } from "@/components/registry/AddressFields";
import { useRegistryRefdata } from "@/components/registry/useRegistryRefdata";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const TITULO = { holder: "Titular (conductor principal)", second_driver: "Segundo conductor" };
const TESTID = { holder: "rd-holder", second_driver: "rd-second" };

// Completar o corregir desde el panel los datos del registro de UNA persona (`rol`: "holder" o "second_driver"). Se
// manda solo esa persona y la API la guarda tal cual: lo que se deja vacío se borra. Concurrencia optimista: va con
// `expected_updated_at`, el `people.updated_at` de la ficha que se cargó (`null` si no había); si entretanto han
// cambiado (el cliente ha hecho el pre-checkin, otro usuario las ha guardado), la API responde 409 y no guarda nada.
// Quitar el segundo conductor es explícito (`remove_second_driver`). Sin canal, el modo básico del pre-checkin
// (`basico={!canal}`): se manda lo que el formulario enseña. No es quien escribe: sin autocompletar con sus datos. Lo
// que el cliente firmó en el pre-checkin no cambia.
// Tras un 409 (han cambiado, o el registro se ha desactivado) se vuelve a leer la ficha (`onStale`): la tarjeta vuelve
// a abrir el diálogo con lo guardado y su versión, o lo cierra si ya no se puede editar.
export default function RegistryPeopleDialog({ reservationId, data, rol, onClose, onSaved, onStale }) {
  const refdata = useRegistryRefdata();
  const canal = data.channel || null;
  const titular = rol === "holder";
  const guardada = data.people?.[rol] || null;
  const version = data.people?.updated_at ?? null;
  // Los valores por defecto, los del tipo de documento guardado: un pasaporte no recibe la nacionalidad ni los países
  // de un DNI.
  const [persona, setPersona] = useState(() => ({ ...personaVacia(guardada?.doc_type || "NIF"), ...(guardada || {}) }));
  const [guardando, setGuardando] = useState(false);
  const [confirmarQuitar, setConfirmarQuitar] = useState(false);

  const enviar = async (body, ok) => {
    setGuardando(true);
    try {
      const { data: v } = await api.put(`/reservations/${reservationId}/registry/people`,
        { ...body, expected_updated_at: version });
      toast.success(ok);
      onSaved(v);
    } catch (err) {
      toast.error(formatError(err));
      if (err?.response?.status === 409) onStale?.();
    } finally {
      setGuardando(false);
    }
  };
  const guardar = () => enviar({ [rol]: limpiarPersona(persona, { basico: !canal, titular }) },
    "Datos del registro guardados");
  const quitar = () => enviar({ remove_second_driver: true }, "Segundo conductor quitado");

  return (
    <Dialog open onOpenChange={(abierto) => { if (!abierto) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{TITULO[rol]}</DialogTitle>
          <DialogDescription className="text-xs text-[#5C5C5C]">
            {canal
              ? "Datos para el registro de viajeros: completa o corrige lo que falta."
              : "La oficina de esta reserva no tiene canal: solo los datos de siempre."}{" "}
            Lo que dejes vacío se borra. Lo que el cliente firmó en el pre-checkin no cambia.
          </DialogDescription>
        </DialogHeader>
        <AvisoTablas refdata={refdata} />
        <PersonFields value={persona} onChange={setPersona} refdata={refdata} canal={canal} testid={TESTID[rol]}
          autoCompletar={false} avisarTablas={false} basico={!canal} titular={titular}
          obligatorios={obligatorios(persona, { canal, titular })} />
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          {!titular && guardada ? (
            <Button type="button" variant="ghost" className="text-[#B54434]" disabled={guardando}
              onClick={() => setConfirmarQuitar(true)} data-testid="rd-remove-second">
              Quitar el segundo conductor
            </Button>
          ) : <span />}
          <Button type="button" onClick={() => guardar()} disabled={guardando} className="bg-marca hover:bg-marca-hover text-white"
            data-testid="rd-save">
            {guardando ? "Guardando…" : "Guardar"}
          </Button>
        </div>
        <AlertDialog open={confirmarQuitar} onOpenChange={setConfirmarQuitar}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Quitar el segundo conductor?</AlertDialogTitle>
              <AlertDialogDescription>
                Se borran sus datos del registro de viajeros de esta reserva. Lo que el cliente firmó en el pre-checkin no
                cambia.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="rd-remove-no">Volver</AlertDialogCancel>
              <AlertDialogAction onClick={quitar} className="bg-[#B54434] hover:bg-[#9C3A2C]" data-testid="rd-remove-ok">
                Quitar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
